import {
  BuyableItemCreationType,
  BuyableItemEvent,
  BuyableItemFindEquivalentType,
  buyableItemHandlerMap,
  BuyableItemOrganiser,
  BuyableItemUpdateType,
  BuyableItemWithEventType,
  BuyableItemWithOrganiserType,
  CompleteBuyableItem,
  isBuyableItemType,
  PartialBuyableItem,
  PartialCompleteBuyableItem,
} from "@ac-expo/api-types";
import { BuyableItem, BuyableItemType, OrderStatus } from "@prisma/client";
import { groupBy } from "lodash";

type OrderWithStatus = {
  status: OrderStatus;
};

type BuyableItemWithOrder<T extends OrderWithStatus> = {
  orderItems?:
    | {
        order: T;
      }[]
    | null;
};

export const getSuccessfulOrderItemOrThrow = <T extends { order: OrderWithStatus }>(
  orderItems: T[],
): T => {
  const successfulOrderItem =
    orderItems.find((item) => item.order.status === OrderStatus.success) ?? null;
  if (!successfulOrderItem) {
    throw new Error("No successful order item found");
  }
  return successfulOrderItem;
};

export const getPrestationBuyableItemOrder = <T extends OrderWithStatus>(
  buyableItem: BuyableItemWithOrder<T>,
): T => {
  const orderItems = buyableItem.orderItems;
  if (!orderItems)
    throw new Error(
      "Buyable item provided is not a Prestation or it contains multiple order items",
    );
  return getSuccessfulOrderItemOrThrow(orderItems).order;
};

type GetBuyableItemNameArg = Pick<BuyableItem, "buyableType">;

export const getBuyableItemName = (buyableItem: GetBuyableItemNameArg) => {
  const metadata = buyableItemHandlerMap[buyableItem.buyableType];
  return metadata?.displayName ?? buyableItem.buyableType;
};

type BuyableItemInput = { buyableType: BuyableItemType } & Record<string, unknown>;

export const getBuyableItemCompleteName = (buyableItem: BuyableItemInput) => {
  const metadata = buyableItemHandlerMap[buyableItem.buyableType];

  if ("getCompleteItemName" in metadata) {
    const name = metadata.getCompleteItemName(buyableItem);
    if (!name) {
      throw new Error("getBuyableItemCompleteName: Invalid item");
    }
    return name;
  }
  return metadata.displayName;
};

export const getBuyableItemEquipmentRequestId = (
  buyableItem: {
    eventEquipmentRequest?: { id: string } | null;
    feeEventEquipmentRequest?: { id: string } | null;
  },
): string | null =>
  buyableItem.eventEquipmentRequest?.id ?? buyableItem.feeEventEquipmentRequest?.id ?? null;

export const getBuyableItemEvent = (
  buyableItem: BuyableItemWithEventType,
): BuyableItemEvent | null => {
  const metadata = buyableItemHandlerMap[buyableItem.buyableType];
  if (!("getEvent" in metadata)) {
    return null;
  }
  return metadata.getEvent(buyableItem as never);
};

export const getBuyableItemOrganiser = (
  buyableItem: BuyableItemWithOrganiserType,
): BuyableItemOrganiser | null => {
  const metadata = buyableItemHandlerMap[buyableItem.buyableType];
  if (!("getOrganiser" in metadata)) {
    return null;
  }
  return metadata.getOrganiser(buyableItem as never);
};

export const isCumulativeBuyableItem = (type: BuyableItemType) => {
  const metadata = buyableItemHandlerMap[type];
  return "isCumulative" in metadata ? metadata.isCumulative : false;
};

export const createBuyableItemArgs = (item: BuyableItemCreationType) => {
  const metadata = buyableItemHandlerMap[item.buyableType];
  return {
    buyableType: item.buyableType,
    ...metadata.create(item as never),
  };
};

export const updateBuyableItemArgs = (item: BuyableItemUpdateType) => {
  const metadata = buyableItemHandlerMap[item.buyableType];
  if (!("update" in metadata)) {
    throw new Error("updateBuyableItemArgs: No update method found");
  }

  return {
    buyableType: item.buyableType,
    ...metadata.update(item as never),
  };
};

export const isUpdatableBuyableItemCreationArgs = (item: BuyableItemCreationType) => {
  const metadata = buyableItemHandlerMap[item.buyableType];
  return "update" in metadata;
};

export const isValidBuyableItem = (item: BuyableItemFindEquivalentType) => {
  const metadata = buyableItemHandlerMap[item.buyableType];
  if (!metadata) return false;
  try {
    return metadata.isValid(item);
  } catch {
    return false;
  }
};

export const isBuyableItem = (item: unknown): item is BuyableItemFindEquivalentType => {
  if (
    typeof item !== "object" ||
    item === null ||
    item === undefined ||
    Array.isArray(item) ||
    !("buyableType" in item) ||
    typeof item.buyableType !== "string" ||
    !isBuyableItemType(item.buyableType)
  ) {
    return false;
  }
  return isValidBuyableItem(item as BuyableItemFindEquivalentType);
};

export const areBuyableItemsEquivalent = (
  item1: BuyableItemFindEquivalentType,
  item2: BuyableItemFindEquivalentType,
): boolean => {
  if (item1.buyableType !== item2.buyableType) return false;
  if (!isValidBuyableItem(item1) || !isValidBuyableItem(item2)) return false;
  return getBuyableItemGroupKey(item1) === getBuyableItemGroupKey(item2);
};

/**
 * Groups items by their buyable item group key.
 * Similar to groupBuyableItemsByEquivalence but uses the group key for faster grouping.
 */
export const groupBuyableItemsByKey = <T extends { buyableItem: BuyableItemFindEquivalentType }>(
  items: T[],
) => {
  return groupBy(items, (item) => getBuyableItemGroupKey(item.buyableItem));
};

export const getFindEquivalentBuyableItemPrismaWhere = (item: BuyableItemFindEquivalentType) => {
  const metadata = buyableItemHandlerMap[item.buyableType];
  return metadata.findEquivalent(item);
};

export const countSameBuyableItems = (
  searchedBuyableItem: CompleteBuyableItem,
  buyableItems: CompleteBuyableItem[],
) => {
  return buyableItems.filter((buyableItem) =>
    areBuyableItemsEquivalent(searchedBuyableItem, buyableItem),
  ).length;
};

/**
 * Gets the Prisma query arguments to retrieve the base pricing for a BuyableItem.
 * Uses the buyableItemMetadataMap to determine where the pricing is stored.
 */
export const getBuyableItemPricingQuery = (buyableItem: PartialBuyableItem) => {
  const metadata = buyableItemHandlerMap[buyableItem.buyableType];
  return metadata.getPricingQuery(buyableItem);
};

/**
 * Gets the general account code for a given buyable item type.
 * This is used for accounting purposes to determine the correct account code.
 */
export const getBuyableItemGeneralAccountCode = (buyableType: BuyableItemType): string => {
  const metadata = buyableItemHandlerMap[buyableType];
  return metadata.accounting.generalAccountCode;
};

/**
 * Gets the invoice label for a given buyable item type.
 * This is used for accounting purposes to determine the correct invoice label.
 */
export const getBuyableItemInvoiceLabel = (buyableType: BuyableItemType): string => {
  const metadata = buyableItemHandlerMap[buyableType];
  return metadata.accounting.invoiceLabel;
};

/**
 * Gets the group key for a buyable item using its handler's getGroupKey method.
 * This is used to group equivalent items together in baskets.
 */
export const getBuyableItemGroupKey = (item: BuyableItemFindEquivalentType): string => {
  const metadata = buyableItemHandlerMap[item.buyableType];
  return metadata.getGroupKey(item);
};

export const createPartialCompleteBuyableItem = (
  buyableItem: BuyableItemCreationType,
): PartialCompleteBuyableItem => {
  if (!isValidBuyableItem(buyableItem)) {
    throw new Error("createPartialCompleteBuyableItem: Invalid item");
  }

  return buyableItem as PartialCompleteBuyableItem;
};

export const parseBuyableItemGroupKeyAsWhere = (
  groupKey: string,
  buyableType?: BuyableItemType,
) => {
  if (!buyableType) {
    buyableType = findGroupKeyBuyableItemTypeOrThrow(groupKey);
  }
  const metadata = buyableItemHandlerMap[buyableType];
  return metadata.parseGroupKeyAsWhere(groupKey);
};

export const isBuyableItemGroupKey = (groupKey: string, buyableType: BuyableItemType) => {
  const metadata = buyableItemHandlerMap[buyableType];
  return metadata.isGroupKey(groupKey);
};

export const findGroupKeyBuyableItemTypeOrThrow = (groupKey: string): BuyableItemType => {
  for (const [buyableType, metadata] of Object.entries(buyableItemHandlerMap)) {
    if (metadata.isGroupKey(groupKey)) {
      if (!isBuyableItemType(buyableType)) {
        throw new Error(`findGroupKeyBuyableItemType: Invalid buyable item type ${buyableType}`);
      }
      return buyableType;
    }
  }

  throw new Error(`findGroupKeyBuyableItemType: No buyable item type found ${groupKey}`);
};

export const getLinkedGroupKeys = (groupKey: string): string[] => {
  const buyableType = findGroupKeyBuyableItemTypeOrThrow(groupKey);
  const metadata = buyableItemHandlerMap[buyableType];
  if (!("getLinkedGroupKeys" in metadata)) {
    return [];
  }
  return metadata.getLinkedGroupKeys(groupKey);
};

/**
 * Builds an array of Prisma filters to search for buyable items by event name.
 * Uses the getEventSearchFilter method from each handler.
 */
export const buildBuyableItemEventSearchFilters = (searchText: string) =>
  Object.values(buyableItemHandlerMap)
    .filter((handler) => "getEventSearchFilter" in handler)
    .flatMap((handler) =>
      handler.getEventSearchFilter ? [handler.getEventSearchFilter(searchText)] : [],
    );

/**
 * Gets all BuyableItemTypes that have a specific invoice label.
 * Used to filter orders by invoice type (reverse lookup from label to types).
 */
export const getBuyableTypesByInvoiceLabel = (invoiceLabel: string): BuyableItemType[] =>
  (
    Object.entries(buyableItemHandlerMap) as [
      BuyableItemType,
      (typeof buyableItemHandlerMap)[BuyableItemType],
    ][]
  )
    .filter(([, handler]) => handler.accounting.invoiceLabel === invoiceLabel)
    .map(([type]) => type);
